import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, Download, X } from 'lucide-react';
import { logDownload } from '../utils/enhancedAnalytics';

const GlobalAudioPlayer = ({ 
  audioUrl, 
  title,
  isPlaying,
  onPlay,
  onPause,
  onClose,
  userId, 
  voiceId 
}) => {
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log("Playback error:", error);
            onPause();
          });
        }
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, audioUrl, onPause]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    const handleError = (e) => {
      console.log('Audio error:', e);
      onPause();
    };

    if (audioRef.current) {
      audioRef.current.addEventListener('error', handleError);
      return () => audioRef.current?.removeEventListener('error', handleError);
    }
  }, [onPause]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
      }
    };
  }, []);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const percentage = x / bounds.width;
    const newTime = percentage * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onPause();
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setDownloadProgress(0);
  
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      await logDownload(userId, audioUrl, voiceId);
      let ext = '.mp3';
      if (title && title.startsWith('Lucid_Audio')) {
        ext = '.mp3';
      } else if (title && title.startsWith('Preview:')) {
        ext = '.mp3';
      }
      const filename = `${title || 'audio'}${ext}`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-0 lg:left-[240px] left-0 right-0 bg-[#2C2F30] border-t border-[#3A3D3E] p-[0.5rem] z-50">
      <div className="max-w-[90rem] mx-auto flex items-center gap-[0.5rem]">
        <button
          onClick={isPlaying ? onPause : onPlay}
          className="p-[0.5rem] rounded-full bg-[#178A94] hover:bg-[#146E75] transition-colors text-[#EAECEF]"
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
        <div className="flex-1">
          <div className="text-[clamp(0.75rem,2vw,1rem)] text-[#B0B4BB] mb-[0.25rem]">
            {title || 'Now Playing'}
          </div>
          <div className="flex items-center gap-[0.25rem]">
            <span className="text-xs text-[#B0B4BB]">{formatTime(currentTime)}</span>
            <div
              className="flex-1 h-[0.25rem] bg-[#181A1B] rounded-full cursor-pointer group"
              onClick={handleSeek}
            >
              <div
                className="h-full bg-[#178A94] rounded-full relative"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] bg-[#EAECEF] rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            {!isNaN(duration) && isFinite(duration) && (
              <span className="text-xs text-[#B0B4BB]">{formatTime(duration)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-[0.5rem]">
          <div className="flex items-center gap-[0.25rem]">
            <Volume2 size={16} className="text-[#B0B4BB]" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-[5rem] h-[0.125rem] bg-[#181A1B] rounded-full appearance-none 
                       [&::-webkit-slider-thumb]:appearance-none 
                       [&::-webkit-slider-thumb]:w-[0.75rem] 
                       [&::-webkit-slider-thumb]:h-[0.75rem] 
                       [&::-webkit-slider-thumb]:rounded-full 
                       [&::-webkit-slider-thumb]:bg-[#178A94]"
              style={{
                        background: `linear-gradient(to right, #EAECEF ${volume * 100}%, #181A1B ${volume * 100}%)`
                      }}
            />
          </div>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="p-[0.5rem] text-[#B0B4BB] hover:text-[#EAECEF] transition-colors"
          >
            {isDownloading ? (
              <div className="flex items-center gap-[0.25rem]">
                <div className="w-[1rem] h-[1rem] border-2 border-[#178A94] border-t-transparent rounded-full animate-spin" />
                <span className="text-[0.75rem]">{Math.round(downloadProgress)}%</span>
              </div>
            ) : (
              <Download size={20} />
            )}
          </button>
          <button
            onClick={handleClose}
            className="p-[0.5rem] text-[#B0B4BB] hover:text-[#EAECEF] transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={onPause}
        />
      </div>
    </div>
  );
};

export default GlobalAudioPlayer;